import { useState, useEffect } from 'react'
import { Loader2, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { shahadaApi, ShahadaLanguage } from '@/services/shahadaApiService'
import ShahadaAudioPlayer from './ShahadaAudioPlayer'
import ShahadaLanguageSelector from './ShahadaLanguageSelector'

interface ShahadaSectionProps {
  defaultLanguageCode?: string
}

export default function ShahadaSection({ defaultLanguageCode }: ShahadaSectionProps) {
  const { i18n } = useTranslation()
  const [availableLanguageCodes, setAvailableLanguageCodes] = useState<Array<{ code: string; name: string; direction: 'ltr' | 'rtl' }>>([])
  const [selectedLanguage, setSelectedLanguage] = useState<ShahadaLanguage | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isLoadingLanguage, setIsLoadingLanguage] = useState(false)
  
  // Get current site language
  const currentSiteLang = defaultLanguageCode || i18n.language.split('-')[0] || 'ar'

  // Load available language codes (hardcoded, not from API)
  useEffect(() => {
    const languageCodes = shahadaApi.getAvailableLanguageCodes()
    setAvailableLanguageCodes(languageCodes)
    
    // Load default language data from API
    loadLanguageData(currentSiteLang)
  }, [])

  const loadLanguageData = (languageCode: string) => {
    try {
      setIsLoadingLanguage(true)
      setError(null)
      
      // Get data directly from hardcoded data (no API call)
      const languageData = shahadaApi.getShahadaByLanguage(languageCode)
      
      if (!languageData) {
        setError(`Language not found: ${languageCode}`)
        return
      }

      setSelectedLanguage(languageData)
    } catch (err) {
      console.error('Error loading language data:', err)
      setError('Failed to load Shahada data')
    } finally {
      setIsLoading(false)
      setIsLoadingLanguage(false)
    }
  }

  const handleLanguageChange = (languageCode: string) => {
    // If language is already selected, do nothing
    if (selectedLanguage?.languageCode === languageCode) {
      return
    }

    setIsLoadingLanguage(true)
    try {
      // Get data directly from hardcoded data (no API call)
      const languageData = shahadaApi.getShahadaByLanguage(languageCode)
      
      if (languageData) {
        setSelectedLanguage(languageData)
      } else {
        setError(`Language not found: ${languageCode}`)
      }
    } catch (err) {
      console.error('Error loading language data:', err)
      setError('Failed to load language data')
    } finally {
      setIsLoadingLanguage(false)
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <section className="py-16 bg-[#f3fbf7] dark:bg-gray-900/40">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading Shahada...</span>
          </div>
        </div>
      </section>
    )
  }

  // Error state
  if (error || !selectedLanguage) {
    return (
      <section className="py-16 bg-[#f3fbf7] dark:bg-gray-900/40">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex flex-col items-center justify-center gap-3 text-gray-600 dark:text-gray-400">
            <AlertCircle className="w-8 h-8" />
            <p>{error || 'Shahada content is not available'}</p>
            <button
              onClick={() => loadLanguageData(currentSiteLang)}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    )
  }

  // Explicitly set RTL based on API response, regardless of browser settings
  const isRTL = selectedLanguage.direction === 'rtl'

  return (
    <section className="py-16 bg-[#f3fbf7] dark:bg-gray-900/40">
      <div 
        className="max-w-4xl mx-auto px-4 text-center"
        dir={isRTL ? 'rtl' : 'ltr'} // Explicit direction from API
      >
        {/* Title - Fixed in Arabic, always shown with explicit RTL */}
        <h2 
          className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4"
          dir="rtl"
          lang="ar"
        >
          ادخل الإسلام الآن
        </h2>

        {/* Subtitle - Fixed in Arabic, always shown with explicit RTL */}
        <p 
          className="text-xl text-gray-700 dark:text-gray-300 mb-8"
          dir="rtl"
          lang="ar"
        >
          للدخول في الإسلام يجب نطق الشهادة
        </p>

        {/* Shahada Calligraphy Image - Always shown */}
        <div className="mb-8 flex justify-center">
          <img
            src={selectedLanguage.calligraphyImageUrl || '/assets/shahada.png'}
            alt="الشهادة"
            className="w-[260px] md:w-[320px] max-w-full h-auto"
            onError={(e) => {
              // Hide image if it fails to load
              const target = e.currentTarget
              target.style.display = 'none'
            }}
          />
        </div>

        {/* Audio Player Card with Language Selector - Figma Design */}
        <div className="relative max-w-md mx-auto">
          {isLoadingLanguage ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-center gap-2 py-4">
                <Loader2 className="w-4 h-4 animate-spin text-gray-600 dark:text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Loading language...</span>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
              {/* Language Selector and Shahada Text - Side by Side (Top) */}
              <div className="flex items-start gap-3 mb-6">
                {/* Language Selector - Right (for RTL languages) or Left (for LTR) */}
                <div className={`flex-shrink-0 pt-1 ${isRTL ? 'order-2' : 'order-1'}`}>
                  <ShahadaLanguageSelector
                    languages={availableLanguageCodes}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={handleLanguageChange}
                  />
                </div>

                {/* Shahada Text - Left (for RTL) or Right (for LTR) - No line break */}
                <div className={`flex-1 ${isRTL ? 'text-right order-1' : 'text-left order-2'}`} dir={selectedLanguage.direction} lang={selectedLanguage.languageCode}>
                  <p className="text-base md:text-lg font-medium text-gray-900 dark:text-gray-100 leading-relaxed whitespace-normal break-words">
                    {selectedLanguage.shahadaText}
                  </p>
                </div>
              </div>

              {/* Audio Player - Full Width (Bottom) */}
              <ShahadaAudioPlayer 
                audioUrl={selectedLanguage.audioUrl} 
                shahadaText={selectedLanguage.shahadaText}
                languageCode={selectedLanguage.languageCode}
                isRTL={isRTL}
                compact={true}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
